import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { ReactComponent as Logo } from "../assets/Icons/onboarding-logo.svg";
import { ReactComponent as ArrowLeft } from "../assets/Icons/arrow-left.svg";
import { useOnboarding } from "../contexts/onboarding";
import { useTeams } from "../contexts/Teams";
import { useLocation, useNavigate } from "react-router-dom";
import paths from "../constants/paths";
import { useAuth } from "../contexts/Auth";
import "./style.css";
import { developmentBaseUrl } from "../constants/common";
import { validateRedirectPath } from "../helper/urlValidation";
import DeviceMetadataTracker from "../components/DeviceMetadataTracker";

const OnboardingLayout = ({ children, withArrow, isLogin }) => {
  const { prev, step } = useOnboarding();
  const { prev: teamsPrev, step: teamsStep } = useTeams();
  const { prev: previous, step: forgetPasswordStep } = useAuth();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isInvitationOnboarding = location.pathname.includes('/teams/acceptInvite');
  const currentStep = isInvitationOnboarding ? teamsStep : step;
  const currentPrev = isInvitationOnboarding ? teamsPrev : prev;
  const back = () => {
    if (location.pathname === paths.forgetPassword) {
      if (forgetPasswordStep > 1) previous();
      else navigate(paths.login);
    } else if (location.pathname === paths.resetPassword) {
      navigate(paths.forgetPassword);
    } else {
      if (currentStep > 1) currentPrev();
      else navigate(-1);
    }
  };

  // The password pages must open even when this browser still remembers a
  // login: someone resetting a forgotten password usually has an old session
  // whose token has expired, and bouncing them to home sends them on to the
  // login page once that token is refused.
  const isPasswordPage =
    location.pathname === paths.forgetPassword ||
    location.pathname === paths.resetPassword;

  useEffect(() => {
    if (isLoggedIn && !isPasswordPage) {
      const redirectPath = sessionStorage.getItem("redirectPath");
      
      // Clear the redirect path immediately to prevent reuse
      sessionStorage.removeItem("redirectPath");
  
      if (redirectPath && redirectPath !== location.pathname && validateRedirectPath(redirectPath)) {
        navigate(redirectPath, { replace: true });
      } else {
        // Always fallback to safe default path
        navigate(paths.home, { replace: true });
      }
    }
  }, [isLoggedIn, isPasswordPage, navigate, location.pathname]);
  

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // justifyContent: "space-between",
        minHeight: "100%",
        pl: "20%",
        pr: "20%"
      }}
    >
      <DeviceMetadataTracker />
      <div className="styleHeader">
        <Container
          className={isLogin || currentStep === 1 ? null : "shadowLayout"}
          sx={{
            position: "sticky",
            top: "0",
            zIndex: "5",
            background: "white",
            paddingBottom: "15px",
          }}
          maxWidth="md"
        >
          <div className="layoutForm">
            {!( isInvitationOnboarding && currentStep === 1) && withArrow ? (
              <IconButton sx={{ p: 0.5, color: "border.dark" }} onClick={back}>
                <ArrowLeft />
              </IconButton>
            ) : (
              <Box width={32} height={32} />
            )}
            <Logo />
            <Box width={32} height={32} />
          </div>
        </Container>
      </div>
      <Box component="main" sx={{ paddingTop: "0px" }} py={5} width="100%">
        <div className="paddingLarge">{children}</div>
      </Box>
      <Box
        sx={{
          textAlign: "center",
          pb: 4.25,
        }}
        component="footer"
      >
        <div className="hideSmall">
          <Typography sx={{ padding: "20px" }} variant="body2">
            <Typography variant="body2" color="text.secondary" component="span">
              ©{new Date().getFullYear()} Trusted Therapist Network™. All rights
              reserved.
              <Typography>
                <a
                  href={`${window.location.origin}/public/privacy`}
                  target="_blank"
                >
                  Privacy Policy
                </a>
                {" | "}
                <a
                  href={window.location.origin + "/public/terms"}
                  target="_blank"
                >
                  Terms of Use
                </a>
                {" | "}
                <a href="mailto:team@thettn.com">Contact Us</a>
                {" | "}
                <a href="mailto:team@thettn.com">Feedback</a>
              </Typography>
            </Typography>
          </Typography>
        </div>
        <div className="hideLg">
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              maxWidth: 300,
              mx: "auto",
              textAlign: "center",
              my: 2,
            }}
          >
            By signing up, you agree to the{" "}
            <a
              sx={{ color: "#0C8CE9 !important" }}
              href={`${window.location.origin}/public/terms`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </a>{" "}
            and{" "}
            <a
              sx={{ color: "#0C8CE9 !important" }}
              href={`${window.location.origin}/public/privacy`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy.
            </a>
          </Typography>
        </div>
      </Box>
    </Box>
  );
};

export default OnboardingLayout;
